/**
 * Le nom de la pièce commerciale : « Offre » ou « Devis ».
 *
 * Réglages → Documents laisse le patron choisir comment ses pièces s'appellent.
 * Le réglage n'était lu que par le titre du document A4 : l'écran disait
 * « Nouvelle offre », le courriel « Devis n° 12 », le PDF « OFFRE », et le
 * client lisait trois mots pour la même pièce. Tout ce qui nomme la pièce à
 * l'utilisateur ou au client passe ici ; les clés techniques (`devis`, routes,
 * API) ne changent pas.
 *
 * Règle « Auto », celle de la maquette : au magasin, « Offre » ; au bâtiment,
 * le mot de la langue de la société — « Offre » en Suisse romande, où c'est
 * l'usage, « Devis » en France. Le serveur applique la même règle
 * (`document/TerminologiePiece`).
 */
import {
  I18N,
  LANG,
  aucunDocument,
  ceDocument,
  estFeminin,
  leDocument,
  modifierLe,
  nouveau,
} from './i18n';

/**
 * Langue dans laquelle la société nomme ses pièces.
 *
 * C'est la règle de l'écran des devis : une société suisse — ou sans pays —
 * parle le français de Suisse, les autres leur langue.
 *
 * @param {object} [soc] société
 * @returns {string} code de langue du dictionnaire
 */
export function langueDe(soc) {
  const pays = soc?.pays;
  return !pays || pays === 'CH' ? 'fr-CH' : soc?.lang || LANG;
}

const majuscule = (m) => (m ? m.charAt(0).toUpperCase() + m.slice(1) : m);

const voyelle = (m) => /^[aeiouyàâéèêëîïôöùûü]/i.test(m);

/**
 * Le nom de la pièce, avec sa majuscule : « Offre » ou « Devis ».
 *
 * @param {object} [soc] société, dont `motOffre` ('' | 'offre' | 'devis'), `pays`, `lang`
 * @param {object} [options] contexte
 * @param {boolean} [options.estMag] vrai dans l'univers Magasin
 * @returns {string} le nom de la pièce
 */
export function motOffre(soc, { estMag } = {}) {
  const choix = soc?.motOffre;
  if (choix === 'offre') return 'Offre';
  if (choix === 'devis') return 'Devis';
  const magasin = estMag ?? soc?.secteur === 'magasin';
  if (magasin) return 'Offre';
  // Le mot français de la région (« Offre » en Suisse romande) : l'écran le
  // traduit ensuite avec la phrase qui l'entoure (« Nouvelle offre » →
  // « Neue Offerte ») ; traduit d'avance dans la langue de l'écran, il
  // donnait « Nouveau offerte ».
  // Seules les variantes du français (fr, fr-CH) choisissent le mot ; une
  // autre langue garde « Devis », que l'écran traduit.
  const langue = langueDe(soc);
  const regional = String(langue).startsWith('fr') ? I18N[langue] : null;
  return majuscule((regional && regional.Devis) || 'Devis');
}

/**
 * Toutes les formes du nom de la pièce, accordées.
 *
 * @param {object} [soc] société
 * @param {object} [options] contexte, voir {@link motOffre}
 * @returns {{
 *   mot: string, Mot: string, MOT: string, pluriel: string, Pluriel: string,
 *   feminin: boolean, le: string, Le: string, ce: string, Ce: string, un: string, Un: string,
 *   du: string, au: string, nouveau: string, modifier: string, generer: string, aucun: string,
 *   supprimer: string, accepte: string, Accepte: string, refuse: string, Refuse: string,
 *   envoye: string, Envoye: string, accord: (masculin: string, feminin: string) => string
 * }} les formes du mot
 */
export function termesOffre(soc, options = {}) {
  const Mot = motOffre(soc, options);
  const mot = Mot.toLowerCase();
  const feminin = estFeminin(mot);
  const accord = (masculin, feminine) => (feminin ? feminine : masculin);
  // « devis » est invariable ; les autres prennent un s.
  const pluriel = /s$/i.test(mot) ? mot : mot + 's';
  const le = leDocument(mot);
  const ce = ceDocument(mot);
  const elide = voyelle(mot);
  return {
    mot,
    Mot,
    MOT: Mot.toUpperCase(),
    pluriel,
    Pluriel: majuscule(pluriel),
    feminin,
    le,
    Le: majuscule(le),
    ce,
    Ce: majuscule(ce),
    un: accord('un ', 'une ') + mot,
    Un: accord('Un ', 'Une ') + mot,
    du: elide ? 'de l’' + mot : accord('du ', 'de la ') + mot,
    au: elide ? 'à l’' + mot : accord('au ', 'à la ') + mot,
    nouveau: nouveau(mot),
    modifier: modifierLe(mot),
    generer: 'Générer ' + le,
    aucun: aucunDocument(mot),
    supprimer: 'Supprimer ' + ce,
    accepte: mot + ' ' + accord('accepté', 'acceptée'),
    Accepte: Mot + ' ' + accord('accepté', 'acceptée'),
    refuse: mot + ' ' + accord('refusé', 'refusée'),
    Refuse: Mot + ' ' + accord('refusé', 'refusée'),
    envoye: mot + ' ' + accord('envoyé', 'envoyée'),
    Envoye: Mot + ' ' + accord('envoyé', 'envoyée'),
    accord,
  };
}

/**
 * Les formes du mot pour une pièce déjà établie : son secteur décide de
 * l'univers, comme sur le document A4.
 *
 * @param {object} [soc] société
 * @param {object} [piece] devis, dont `sect` ou `estMag`
 * @returns {ReturnType<typeof termesOffre>} les formes du mot
 */
export function termesDeLaPiece(soc, piece) {
  const estMag = piece?.sect ? piece.sect === 'magasin' : piece?.estMag ? true : undefined;
  return termesOffre(soc, { estMag });
}
